import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { 
  LayoutDashboard, 
  PlusCircle, 
  History, 
  FolderOpen, 
  LogOut,
  BarChart3,
  Menu,
  X,
  MessageCircle,
  Settings as SettingsIcon,
  Images
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/new-analysis", icon: PlusCircle, label: "Nova Análise" },
  { path: "/history", icon: History, label: "Histórico" },
  { path: "/categories", icon: FolderOpen, label: "Categorias" },
  { path: "/gabaritos", icon: Images, label: "Gabaritos" },
  { path: "/whatsapp", icon: MessageCircle, label: "WhatsApp" },
  { path: "/settings", icon: SettingsIcon, label: "Configurações" },
];

export default function Sidebar({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-zinc-200">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-900 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex flex-col">
            <span className="font-chivo font-bold text-sm tracking-tight leading-tight">Monitoramento</span>
            <span className="font-chivo font-bold text-sm tracking-tight leading-tight">de Ocupação</span>
          </div>
        </Link>
        <p className="text-xs text-zinc-500 mt-1 ml-12">Smart Break</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                    ${isActive 
                      ? "bg-zinc-900 text-white" 
                      : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                    }`}
                >
                  <item.icon size={18} strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User */}
      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 px-4 py-2 mb-2">
          <div className="w-8 h-8 bg-zinc-200 flex items-center justify-center text-sm font-medium">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-3 w-full px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
        >
          <LogOut size={18} strokeWidth={1.5} />
          Sair
        </button>
      </div>

      {/* Footer - Powered by CrowdSA */}
      <div className="p-4 border-t border-zinc-100 bg-zinc-50">
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-400">Powered by</span>
            <a href="https://www.crowdsa.com.br" target="_blank" rel="noopener noreferrer">
              <img 
                src="/crowdsa-logo.png" 
                alt="CrowdSA" 
                className="h-4 w-auto hover:opacity-80 transition-opacity cursor-pointer"
              />
            </a>
          </div>
          <p className="text-[10px] text-zinc-400 text-center">
            All rights reserved 2026 CrowdSA.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-chivo font-bold text-sm">Smart Break</span>
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 hover:bg-zinc-100"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-zinc-200 z-50 transform transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <NavContent />
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 bg-white border-r border-zinc-200">
        <NavContent />
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}
